import { EllipsisVertical } from "lucide-react";
import React, { MouseEvent } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";
import { v4 } from "uuid";

import { IgIconButton, IgTypography } from "@intelligems/ig-theme";
import {
  ExperienceListVariationDto,
  GetScriptStatusResponseBodyType,
  OrganizationDto,
  ScheduleDto,
  TabEnumFe,
  VariationFe,
} from "@intelligems/ig-types";
import { checkOfferLimit } from "@intelligems/ig-utils/offer";
import { Menu, MenuItem } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useDeleteExperience } from "@src/api/experience-actions/delete-experience";
import { useDuplicateExperience } from "@src/api/experiences/duplicate-experience";
import { usePatchExperience } from "@src/api/experiences/patch-experience";
import { useGetExperiencesList } from "@src/api/experiences-list/get-experiences-list";
import { processesKeys } from "@src/api/queryKeys";
import { UpgradePlanText } from "@src/components/LimitAccess/access-text";
import { ScopedCreateExperienceWithDisabled } from "@src/components/LimitAccess/ScopedCreateExperienceWithDisabled";
import { ScopedStartExperienceWithDisabled } from "@src/components/LimitAccess/ScopedStartExperienceWithDisabled";
import { EXPERIMENT_ROUTES, PERSONALIZATION_ROUTES } from "@src/routes/routes";
import { openDrawer } from "@src/store/slices/drawers";
import { Experience } from "@src/store/slices/experiences/types";
import { setDialog } from "@src/store/slices/global";
import { FoundExperimentDetails } from "@src/types/experience";
import { generatePreviewLink } from "@src/utils/generatePreviewLink";
import {
  mpClickDuplicateCampaignButton,
  mpClickEditCampaignButton,
} from "@src/utils/mixpanel";
import { useQueryClient } from "@tanstack/react-query";

export interface MoreMenuPersonalizationProps {
  isUsedInTest: FoundExperimentDetails;
  experience: Experience;
  variations: (ExperienceListVariationDto | VariationFe)[];
  organization: OrganizationDto;
  renderLocation: "overview" | "experience" | "offers";
  onArchive?: () => void;
  onUnarchive?: () => void;
  onDelete?: () => void;
  schedules: ScheduleDto[];
  isSchedulesLoading: boolean;
  scriptStatus: GetScriptStatusResponseBodyType | null;
}

export const MoreMenuPersonalization = ({
  isUsedInTest,
  experience,
  organization,
  variations,
  renderLocation,
  schedules,
  isSchedulesLoading,
  onArchive,
  onUnarchive,
  onDelete,
  scriptStatus,
}: MoreMenuPersonalizationProps) => {
  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(window.location.search);

  const navigate = useNavigate();
  const deleteExperience = useDeleteExperience(
    { ...experience, id: experience.id! },
    "personalization"
  );
  const patchExperience = usePatchExperience(experience?.id);
  const duplicateExperience = useDuplicateExperience("personalization");
  const queryClient = useQueryClient();
  const personalizationsList = useGetExperiencesList({
    category: "personalization",
  });
  const experienceList = useGetExperiencesList({ category: "experiment" });
  const [anchorEl, setAnchorEl] = useImmer<null | HTMLElement>(null);

  const open = Boolean(anchorEl);
  const enableScheduleTests = useConfiguration<boolean>(
    "enable_schedule_tests"
  )!.value;

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onDeleteExperience = (campaignId: string) => {
    if (onDelete) {
      onDelete();
    }
    deleteExperience.mutate({
      body: { variationId: undefined },
      queryParams: undefined,
      pathParams: {
        experienceId: campaignId,
        action: "delete",
      },
    });

    handleClose();
  };

  const handleArchive = () => {
    if (onArchive) {
      onArchive();
    }
    dispatch(
      setDialog({
        dialog: "archivePersonalization",
        open: true,
        props: {
          experienceId: experience.id,
          shouldNavigate: renderLocation !== "offers",
        },
      })
    );
    handleClose();
  };

  const handleUnarchive = () => {
    const isUnderLimit = checkOfferLimit(
      organization,
      personalizationsList.data?.experiencesList,
      experienceList.data?.experiencesList
    );
    if (!isUnderLimit) {
      dispatch(setDialog({ dialog: "offerLimitReached", open: true }));
      handleClose();
      return;
    }

    if (onUnarchive) {
      onUnarchive();
    }
    dispatch(
      setDialog({
        dialog: "unarchivePersonalization",
        open: true,
        props: {
          experienceId: experience.id,
          shouldNavigate: renderLocation !== "offers",
        },
      })
    );
    handleClose();
  };

  const handleViewInfo = () => {
    dispatch(
      openDrawer({
        type: "experienceInfo",
        experience,
        isUsedInTest,
      })
    );
    handleClose();
  };

  return (
    <>
      <IgIconButton
        size={"small"}
        id="personalization-more-button"
        aria-controls="more-positioned-menu"
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        loading={patchExperience.isPending || duplicateExperience.isPending}
      >
        <EllipsisVertical size={16} />
      </IgIconButton>
      <Menu
        id="more-positioned-menu"
        aria-labelledby="more-positioned-button"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        {(renderLocation === "overview" || renderLocation === "offers") && (
          <Link
            style={{ textDecoration: "none" }}
            to={`${PERSONALIZATION_ROUTES.Personalization}/${experience?.id}?action=edit`}
            state={{
              isUsedInActiveTest: isUsedInTest,
              discountId: variations[0].offerId,
            }}
            onClick={() => {
              mpClickEditCampaignButton();
            }}
          >
            <MenuItem>
              <IgTypography>
                {experience.archivedAtTs ? "View" : "View and edit"}
              </IgTypography>
            </MenuItem>
          </Link>
        )}
        {experience.id && !experience.archivedAtTs && (
          <ScopedStartExperienceWithDisabled
            experience={experience}
            debugId={"schedule-personalization"}
            customDisabledTooltipMessage={UpgradePlanText}
            additionalEnableRequirements={[enableScheduleTests]}
          >
            <MenuItem
              disabled={!enableScheduleTests || isSchedulesLoading}
              onClick={() => {
                handleClose();
                dispatch(
                  setDialog({
                    dialog: "schedulePersonalization",
                    open: true,
                    props: {
                      experience,
                      schedules,
                    },
                  })
                );
              }}
            >
              <IgTypography>
                {schedules?.length
                  ? "Edit or cancel schedule"
                  : `Schedule ${renderLocation === "offers" ? "offer" : "experience"}`}
              </IgTypography>
            </MenuItem>
          </ScopedStartExperienceWithDisabled>
        )}
        {experience.id !== undefined && (
          <MenuItem onClick={handleViewInfo}>
            <IgTypography>Show info</IgTypography>
          </MenuItem>
        )}
        {experience.id !== undefined && (
          <MenuItem
            onClick={() => {
              if (!scriptStatus?.exists) {
                dispatch(
                  setDialog({
                    dialog: "redirectToPluginDialog",
                    open: true,
                    props: {
                      id: experience.id,
                      experience,
                      path: generatePreviewLink({ organization, experience }),
                    },
                  })
                );
              } else {
                window.open(
                  generatePreviewLink({ organization, experience }),
                  "_blank"
                );
              }
              handleClose();
            }}
          >
            <IgTypography>Preview</IgTypography>
          </MenuItem>
        )}
        {!experience.archivedAtTs && !!experience?.id ? (
          <ScopedCreateExperienceWithDisabled
            experience={experience}
            debugId={"duplicate-personalization"}
            customDisabledTooltipMessage={UpgradePlanText}
          >
            <MenuItem
              disabled={!experience?.id}
              style={{ textDecoration: "none", color: "black" }}
              onClick={() => {
                handleClose();
                queryClient.setQueryData(processesKeys.one(organization.id), {
                  processes: [
                    {
                      id: v4(),
                      orgId: organization.id,
                      type: "duplicateExperience",
                      severity: "info",
                      title: `Duplicating ${experience?.name}`,
                      isLoading: true,
                    },
                  ],
                });
                if (experience.id) {
                  duplicateExperience.mutate({
                    body: undefined,
                    pathParams: { experienceId: experience.id },
                    queryParams: undefined,
                  });
                }
                mpClickDuplicateCampaignButton();
              }}
            >
              <IgTypography>Duplicate</IgTypography>
            </MenuItem>
          </ScopedCreateExperienceWithDisabled>
        ) : null}
        {isUsedInTest.id && (
          <MenuItem
            onClick={() => {
              void navigate(
                `${EXPERIMENT_ROUTES.Experiment}/${isUsedInTest.id}?action=edit&tab=${TabEnumFe.MODS}&type=personalization/offer`
              );
              handleClose();
            }}
          >
            <IgTypography>View test</IgTypography>
          </MenuItem>
        )}
        {experience.id !== undefined && (
          <MenuItem
            onClick={() => {
              dispatch(
                openDrawer({
                  type: "history",
                  experienceType: "personalization/offer",
                  experienceInfo:
                    renderLocation === "overview" || renderLocation === "offers"
                      ? experience
                      : undefined,
                })
              );
              handleClose();
            }}
          >
            <IgTypography>View history</IgTypography>
          </MenuItem>
        )}
        {!experience.archivedAtTs ? (
          experience.status !== "started" && (
            <MenuItem disabled={!experience?.id} onClick={handleArchive}>
              <IgTypography>Archive</IgTypography>
            </MenuItem>
          )
        ) : (
          <MenuItem disabled={!experience?.id} onClick={handleUnarchive}>
            <IgTypography>Unarchive</IgTypography>
          </MenuItem>
        )}
        {(renderLocation === "overview" || renderLocation === "offers") &&
          experience.id !== undefined &&
          experience.status !== "started" &&
          !schedules.length && (
            <MenuItem
              onClick={() => {
                if (experience.id) {
                  onDeleteExperience(experience.id);
                }
              }}
            >
              <IgTypography>Delete</IgTypography>
            </MenuItem>
          )}
        {renderLocation === "experience" &&
          searchParams.get("tab") === TabEnumFe.ANALYTICS &&
          experience.status !== "pending" && (
            <MenuItem
              onClick={() => {
                dispatch(setDialog({ dialog: "dataExport", open: true }));
                handleClose();
              }}
            >
              <IgTypography>Export data</IgTypography>
            </MenuItem>
          )}
      </Menu>
    </>
  );
};
